import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { capi, companyUrl } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { useToast } from '../components/Toast';
import { Button, Card, Chip, Loading, Modal } from './ui';

interface LayoutResponse {
  layoutHtml: string | null;
  defaultLayoutHtml: string;
  presets?: Array<{ id: string; name: string; html: string }>;
  fields: Record<string, readonly string[]>;
}

/**
 * Editor for a company's invoice layout.
 *
 * The preview posts the *unsaved* draft and renders it server-side, so an
 * author sees the real PDF — the same renderer that produces the invoice —
 * before committing a layout that every future invoice will use.
 */
export function LayoutEditor() {
  const { company, role } = useAuth();
  const { t } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);

  const q = useQuery({ queryKey: ['invoice-layout', company?.id], queryFn: () => capi<LayoutResponse>('/invoice-layout') });
  const custom = !!q.data?.layoutHtml;

  return (
    <>
      <Card>
        <h2 style={{ marginBottom: '0.3rem' }}>{t('layoutTitle')}</h2>
        <p className="secondary" style={{ margin: '0 0 0.8rem', fontSize: '0.8rem' }}>{t('layoutSub')}</p>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Chip tone={custom ? 'primary' : 'neutral'}>{custom ? t('layoutCustom') : t('layoutBuiltIn')}</Chip>
          <Button size="sm" icon="fas fa-code" onClick={() => setOpen(true)} disabled={!q.data}>
            {role === 'admin' ? t('layoutEdit') : t('layoutView')}
          </Button>
        </div>
      </Card>
      {open && q.data && <LayoutModal data={q.data} onClose={() => setOpen(false)} onSaved={() => {
        void qc.invalidateQueries({ queryKey: ['invoice-layout'] });
        toast.push(t('done'));
        setOpen(false);
      }} />}
    </>
  );
}

function LayoutModal({ data, onClose, onSaved }: { data: LayoutResponse; onClose: () => void; onSaved: () => void }) {
  const { role } = useAuth();
  const { t } = useI18n();
  const toast = useToast();
  const [html, setHtml] = useState(data.layoutHtml ?? data.defaultLayoutHtml);
  const [letter, setLetter] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const objectUrl = useRef<string | null>(null);
  const readOnly = role !== 'admin';

  // Object URLs are revoked on replacement and on unmount; leaving them alive
  // would pin each rendered PDF in memory for the life of the tab.
  useEffect(() => () => { if (objectUrl.current) URL.revokeObjectURL(objectUrl.current); }, []);

  async function preview() {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const res = await fetch(companyUrl('/invoice-layout/preview'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ layoutHtml: html, letter }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? res.statusText);
      }
      const blob = await res.blob();
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = URL.createObjectURL(blob);
      setPreviewUrl(objectUrl.current);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : String(err));
    } finally {
      setPreviewing(false);
    }
  }

  const save = useMutation({
    mutationFn: (value: string | null) => capi('/settings', { method: 'PATCH', json: { invoiceLayoutHtml: value } }),
    onSuccess: onSaved,
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const fieldGroups = useMemo(() => Object.entries(data.fields), [data.fields]);

  return (
    <Modal title={t('layoutTitle')} onClose={onClose}>
      <div className="grid two" style={{ gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', gap: '1rem', alignItems: 'start' }}>
        <div className="stack md">
          <textarea
            value={html}
            readOnly={readOnly}
            spellCheck={false}
            onChange={(e) => setHtml(e.target.value)}
            aria-label={t('layoutTitle')}
            style={{
              width: '100%', minHeight: 420, fontFamily: 'var(--font-mono, monospace)', fontSize: '0.72rem',
              lineHeight: 1.5, padding: '0.6rem', borderRadius: 'var(--radius-xs)',
              border: '1px solid var(--glass-border)', background: 'var(--input-bg)', color: 'var(--text-primary)',
            }}
          />
          <div className="row" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
            <Button size="sm" icon="fas fa-eye" onClick={() => void preview()} disabled={previewing}>
              {previewing ? t('layoutRendering') : t('layoutPreview')}
            </Button>
            <label className="row small secondary" style={{ gap: '0.35rem', alignItems: 'center' }}>
              <input type="checkbox" checked={letter} onChange={(e) => setLetter(e.target.checked)} />
              {t('layoutAsLetter')}
            </label>
            <div className="spacer" />
            {!readOnly && (
              (data.presets ?? [{ id: 'default', name: 'AviaConsole', html: data.defaultLayoutHtml }]).map((p) => (
                <Button key={p.id} size="sm" onClick={() => setHtml(p.html)}>{t('layoutLoadPreset', { name: p.name })}</Button>
              ))
            )}
          </div>
          {previewError && <div className="small" style={{ color: 'var(--danger, #b91c1c)' }}>{previewError}</div>}
        </div>

        <div className="stack md">
          <div>
            <div className="small secondary" style={{ marginBottom: '0.4rem' }}>{t('layoutFieldsHint')}</div>
            <div className="stack" style={{ gap: '0.5rem', maxHeight: 200, overflowY: 'auto' }}>
              {fieldGroups.map(([group, fields]) => (
                <div key={group}>
                  <div className="small muted" style={{ marginBottom: '0.2rem' }}>{group}</div>
                  <div className="row" style={{ gap: '0.3rem', flexWrap: 'wrap' }}>
                    {fields.map((f) => (
                      <span key={f} className="var" style={{ cursor: readOnly ? 'default' : 'pointer' }}
                        onClick={() => !readOnly && setHtml((h) => `${h}{{${group}.${f}}}`)}>
                        {`{{${group}.${f}}}`}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="small secondary">{t('layoutBlocksHint')}</div>
          {previewUrl
            ? <iframe title={t('layoutPreview')} src={previewUrl} style={{ width: '100%', height: 420, border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-xs)', background: '#fff' }} />
            : <div className="a4" style={{ display: 'grid', placeItems: 'center', minHeight: 200 }}><span className="small muted">{t('layoutPreviewHint')}</span></div>}
        </div>
      </div>

      <div className="row" style={{ justifyContent: 'flex-end', marginTop: '1rem' }}>
        {!readOnly && data.layoutHtml && (
          <Button variant="danger" onClick={() => { if (confirm(t('layoutConfirmReset'))) save.mutate(null); }}>
            {t('layoutReset')}
          </Button>
        )}
        <div className="spacer" />
        <Button onClick={onClose}>{t('cancel')}</Button>
        {!readOnly && <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate(html)}>{t('save')}</Button>}
      </div>
    </Modal>
  );
}

export { Loading };
